import { useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Reveal, EASE } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import PostCard from "@/components/PostCard";
import HEROES from "@/lib/pageHeroes.json";


const CATEGORIES = ["announcement", "jv-milestone", "sector-insight"];

export default function News() {
  const { t } = useLang();
  const site = useSite();
  const posts = site?.posts || [];
  const [category, setCategory] = useState("all");

  const shown = useMemo(
    () => (category === "all" ? posts : posts.filter((p) => p.category === category)),
    [posts, category]
  );
  const [lead, ...rest] = shown;
  const featuredLead = category === "all" && lead;

  return (
    <div data-testid="news-page">
      <Seo page="news" />
      <PageHero eyebrow={t("news_eyebrow")} title={t("news_title")} sub={t("news_sub")} image={HEROES["news"]} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 md:py-24">
        <div className="mb-12 flex flex-wrap gap-2 border-b border-border pb-6" data-testid="news-category-filters">
          {[{ id: "all", label: t("news_all") }, ...CATEGORIES.map((c) => ({ id: c, label: t(`cat_${c}`) }))].map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`news-category-${c.id}`}
              onClick={() => setCategory(c.id)}
              className={`h-9 rounded-full px-4 text-xs font-semibold tracking-wide transition-colors ${
                category === c.id ? "bg-primary text-primary-foreground" : "border border-border text-foreground/70 hover:text-primary"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {shown.length === 0 && (
          <p className="py-20 text-center text-sm text-muted-foreground" data-testid="news-empty">{t("news_none")}</p>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={category}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="space-y-8"
          >
            {featuredLead && (
              <Reveal>
                <PostCard post={lead} featured />
              </Reveal>
            )}
            <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
              {(featuredLead ? rest : shown).map((p, i) => (
                <Reveal key={p.id} delay={0.05 * (i % 3)}>
                  <PostCard post={p} />
                </Reveal>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </section>
    </div>
  );
}
